import React, { useCallback, useContext, useEffect, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import MessagesListContainer from 'supportAssistant/components/Chat/MessagesListContainer'
import MessageInput from 'supportAssistant/components/Chat/MessageInput'
import Header from 'supportAssistant/components/Chat/Header'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import FeedbackModal from 'supportAssistant/components/Chat/FeedbackModal'
import { postFeedback } from 'api/support'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { MessageSenders } from 'supportAssistant/constants/messageSenders'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { ChatStatuses } from 'supportAssistant/constants/chatStatuses'
import RadioButtonInput from 'components/Luxkit/RadioButton/RadioButtonInput'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import DisclaimerModal from 'supportAssistant/components/Chat/DisclaimerModal'
import SliderInput from 'components/Luxkit/Slider/SliderInput'
import config from 'constants/config'
import { shallowEqual } from 'react-redux'
import { useAppSelector } from '../../../client/hooks/reduxHooks'
import { Agent } from 'supportAssistant/components/Support/Agent'

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr min-content auto;
  grid-template-areas:
    "messages"
    "suggestions"
    "messageInput";
  height: 100%;
  width: 100%;
  position: relative;
  padding: ${rem(24)};
  padding-top: ${rem(84)};
  background-color: ${({ theme }) => theme.palette.neutral.eight};
`

const HeaderContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
`

const StyledMessagesListContainer = styled(MessagesListContainer)`
  grid-area: messages;
`

const SuggestionsContainer = styled.div`
  grid-area: suggestions;
  display: grid;
  justify-items: end;
  grid-gap: ${rem(8)};
  margin-bottom: ${rem(12)};
`

const MessageInputContainer = styled(MessageInput)`
  grid-area: messageInput;
`

const ChatClosedBodyTextBlock = styled(BodyTextBlock)`
  margin-bottom: ${rem(56)};
`

const ChatClosedDiv = styled.div`
  display: flex;
  margin-top: ${rem(16)};
  margin-bottom: ${rem(12)};
  flex-direction: column;
`

const NewChatAssistChip = styled(AssistChip)`
  align-self: end;
`

const MessagesInputSkeleton = styled(LoadingBox)`
  grid-area: messageInput;
  height: ${rem(46)};
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.medium};
`

const SuggestionsSkeleton = styled(LoadingBox)`
  border-radius: ${props => props.theme.borderRadius.round};
  height: ${rem(32)};
  width: 50%;
`

const StyledGroup = styled(Group)`
  margin-bottom: ${rem(8)};
`

const StyledDisclaimerTextBlock = styled(BodyTextBlock)`
  margin-top: ${rem(12)};
  margin-bottom: ${rem(4)};
`

interface ChatProps {
  userId?: string;
  socket: any;
  activeChat?: any;
  onNewChat: () => void;
  toggleHistory?: () => void;
  loadingMessages?: boolean;
}

const SUGGESTIONS = [
  {
    text: 'I didn’t receive my confirmation email',
  },
  {
    text: 'Can I cancel my hotel booking?',
  },
  {
    text: 'Where can I see all my inclusions?',
  },
  {
    text: 'How can I change my dates?',
  },
]

const EMPTY = ''
const STUFF = 'stuff'
const MAP_REDUCE = 'map_reduce'
const REFINE = 'refine'

const Chat: React.FC<ChatProps> = ({
  socket,
  activeChat,
  onNewChat,
  toggleHistory,
  userId,
  loadingMessages,
}) => {
  const [messages, setMessages] = useState<any[]>([])
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [queuedMessage, setQueuedMessage] = useState<any>(null)
  const [outageMessage, setOutageMessage] = useState<string | undefined>(undefined)

  // debugOptions
  const [qaChainType, setqaChainType] = useState<string>(MAP_REDUCE)
  const [embeddingThreshold, setEmbeddingThreshold] = useState<number>(0.2)

  const showSuggestions = activeChat?.status !== ChatStatuses.CLOSED && !messages?.length
  const isLastMessageByUser = messages[messages.length - 1]?.sender === MessageSenders.USER

  const [firstName, lastName] = useAppSelector((state: App.State) => [state.auth.account.givenName, state.auth.account.surname], shallowEqual)

  useEffect(() => {
    setMessages(activeChat?.messages || [])
  }, [activeChat?.messages])

  useEffect(() => {
    setIsTyping(false)
  }, [activeChat, loadingMessages])

  // first message, when chat history is empty and there
  // is no active chat, creates new chat and gets sent to the server
  useEffect(() => {
    if (activeChat?.id && queuedMessage) {
      socket.emit('message', {
        ...queuedMessage,
        chatId: activeChat?.id,
      })

      setMessages((prevMessages) => [...prevMessages, { ...queuedMessage, sender: MessageSenders.USER }])
      setQueuedMessage(null)
    }
  }, [activeChat?.id, queuedMessage, socket])

  useEffect(() => {
    if (socket) {
      socket.on('message:response', (newMessage) => {
        if (newMessage.chatId === activeChat?.id) {
          setIsTyping(false)
          setMessages(prevMessages => [...prevMessages, newMessage])
        }
      })

      return () => socket.off('message:response')
    }
  }, [socket, activeChat])

  useEffect(() => {
    if (socket) {
      socket.on('response:typing', () => {
        setIsTyping(true)
      })

      socket.on('chat:error:service', (error) => {
        setIsTyping(false)
        setOutageMessage(error.text)
      })

      return () => {
        socket.off('response:typing')
        socket.off('chat:error:service')
      }
    }
  }, [socket])

  const onMessageSend = useCallback((message: string, system: boolean = false) => {
    const trimmedMessage = message.trim()

    if (!trimmedMessage) {
      return
    }

    const debugOptions = config.SUPPORT_ASSISTANT_DEBUG ? { debugOptions: { qaChainType, embeddingThreshold } } : {}
    const messageData = {
      text: trimmedMessage,
      chatId: activeChat?.id,
      ...debugOptions,
      customer: {
        firstName,
        lastName,
      },
    }

    // first message, when chat history is empty and there
    // is no active chat, should create new chat
    if (!activeChat?.id) {
      socket.emit('chat:create', {
        customerId: userId,
      })

      setQueuedMessage(messageData)

      return
    }

    const sender = system ? MessageSenders.SYSTEM : MessageSenders.USER

    setMessages((prevMessages) => [...prevMessages, { ...messageData, sender }])

    socket.emit('message', { ...messageData, sender })
  }, [qaChainType, embeddingThreshold, activeChat?.id, firstName, lastName, socket, userId])

  const showModal = useContext(ModalContext)

  const submitMessageFeedback = useCallback((feedback) => {
    socket.emit('message:feedback', feedback)
  }, [socket])

  const submitFeedback = useCallback((feedback) => {
    postFeedback({
      chatId: activeChat?.id,
      customerId: userId,
      ...feedback,
    })
      .then(() => showSnackbar(
        'Thank you for your valuable feedback. It greatly helps us improve the user experience.',
        'success',
        { heading: 'Feedback successfully submitted' },
      ))
      .catch(() => showSnackbar(
        'Something went wrong. Please try again later.',
        'warning',
        { heading: 'Error' },
      ))
  }, [activeChat?.id, userId])

  const toggleFeedbackModal = useCallback(async() => {
    const feedback = await showModal<undefined | any>(<FeedbackModal />)

    if (feedback.issues?.length) {
      submitFeedback(feedback)
    }
  }, [showModal, submitFeedback])

  const toggleDisclaimerModal = useCallback(() => {
    showModal<undefined | any>(<DisclaimerModal />)
  }, [showModal])

  const onMessageFeedbackSubmit = useCallback(async(userFeedback) => {
    const messageFeedback = {
      feedback: userFeedback.sentiment,
      chatId: activeChat?.id,
      messageId: userFeedback.messageId,
    }

    submitMessageFeedback(messageFeedback)
    submitFeedback(userFeedback)
  }, [activeChat?.id, submitFeedback, submitMessageFeedback])

  return (
    <StyledContainer>
      <HeaderContainer>
        <Header
          toggleHistory={toggleHistory}
          toggleFeedback={toggleFeedbackModal}
          disableReportFeedback={!activeChat}
        />
      </HeaderContainer>

      <StyledMessagesListContainer
        loadingMessages={loadingMessages}
        messages={messages}
        isTyping={isTyping}
        chatCreatedAt={activeChat?.createdAt}
        onMessageFeedbackSubmit={onMessageFeedbackSubmit}
      />

      {!loadingMessages && activeChat?.status === ChatStatuses.CLOSED && (
        <ChatClosedDiv>
          <ChatClosedBodyTextBlock variant="small" align="center">
            This chat is timed out. Please start a new chat.
          </ChatClosedBodyTextBlock>
          <NewChatAssistChip variant="filled" size="medium" onClick={onNewChat}>
            Start new chat
          </NewChatAssistChip>
        </ChatClosedDiv>
      )}

      <SuggestionsContainer>
        {loadingMessages &&
          SUGGESTIONS.map((suggestion) => (
            <SuggestionsSkeleton key={suggestion.text} />
          ))}
        {!loadingMessages && showSuggestions && (
          <>
            <BodyTextBlock variant="small">
              Frequently Asked Questions
            </BodyTextBlock>
            {SUGGESTIONS.map((suggestion) => (
              <AssistChip
                key={`suggestion-${suggestion.text}`}
                variant="filled"
                size="medium"
                onClick={() => onMessageSend(suggestion.text)}
              >
                {suggestion.text}
              </AssistChip>
            ))}
          </>
        )}
        <Agent
          socket={socket}
          activeChat={activeChat}
          onMessageSend={onMessageSend}
          customer={{
            firstName,
            lastName,
          }}
        />
      </SuggestionsContainer>

      {outageMessage && (
        <ChatClosedDiv>
          <MessageBanner
            description={outageMessage}
            heading=""
            kind="critical"
          />
        </ChatClosedDiv>
      )}

      {config.SUPPORT_ASSISTANT_DEBUG && (
        <StyledGroup direction="horizontal" gap={8} wrap="wrap">
          <SliderInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmbeddingThreshold(parseFloat(e.target.value))}
            defaultValue={embeddingThreshold}
            min={0}
            max={1}
            step={0.001}
            variant="label"
          />
          <RadioButtonInput
            key="qaChainTypeEmpty"
            checked={qaChainType === EMPTY}
            value="true"
            name="qaChainType"
            required
            onChange={() => setqaChainType(EMPTY)}
            >
            Empty
          </RadioButtonInput>
          <RadioButtonInput
            key="qaChainTypeStuff"
            checked={qaChainType === STUFF}
            value="true"
            name="qaChainType"
            required
            onChange={() => setqaChainType(STUFF)}
            >
            stuff
          </RadioButtonInput>
          <RadioButtonInput
            key="qaChainTypeMap_reduce"
            checked={qaChainType === MAP_REDUCE}
            value="false"
            name="qaChainType"
            required
            onChange={() => setqaChainType(MAP_REDUCE)}
            >
            map_reduce
          </RadioButtonInput>
          <RadioButtonInput
            key="qaChainTypeRefine"
            checked={qaChainType === REFINE}
            value="false"
            name="qaChainType"
            required
            onChange={() => setqaChainType(REFINE)}
            >
            refine
          </RadioButtonInput>
        </StyledGroup>
      )}
      {loadingMessages && <MessagesInputSkeleton />}
      {!loadingMessages && (
        <MessageInputContainer
          onMessageSend={onMessageSend}
          isTyping={isTyping}
          chatStatus={activeChat?.status}
          chatAgentType={activeChat?.agentType}
          isLastMessageByUser={isLastMessageByUser}
          isDown={!!outageMessage}
        />
      )}
      <StyledDisclaimerTextBlock variant="small">
        Information provided is general in nature, may be inaccurate or not apply to your particular booking.{' '}
        <TextLink
          weight="regular"
          onClick={toggleDisclaimerModal}
        >
          Find out more
        </TextLink>
      </StyledDisclaimerTextBlock>
      <BodyTextBlock variant="small">
        Please do not send private or sensitive information, such as credit card details or passwords.{' '}
        <TextLink weight="regular" href="/privacy-policy" target="_blank">
          View privacy policy
        </TextLink>
      </BodyTextBlock>
    </StyledContainer>
  )
}

export default Chat
